import React, { lazy, Suspense, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Routes } from 'react-router-dom'

import Alert from './common/Alert'
import PageFrame from './common/PageFrame'
import SecureRoute from './common/SecureRoute'
import Dashboard from './pages/Dashboard/Dashboard'
import Error from './pages/Error'
import Logout from './pages/Login/Logout'
import SelectOrgAccount from './pages/Login/SelectOrgAccount'
import OrganizationAdmin from './pages/Organization/OrganizationAdmin'


import AsyncComponent from 'admin/components/common/AsyncComponent'
import Text from 'admin/components/common/Text'
import { State } from 'admin/redux/ducks'
import { LoginState } from 'admin/redux/ducks/login'
import Context from 'admin/services/Context'
import { FormActionType } from 'admin/types'
import ErrorBoundary from 'components/ui/ErrorBoundary'
import Loader from 'components/ui/Loader'
import LastLocationProvider from 'services/LastLocationProvider'
import Locale from 'services/locale'
import { AdminTypes, ConfigType } from 'types'

const Authenticate = lazy(() => import('./pages/Login/Authenticate'))
const Organization = lazy(() => import('./pages/Organization/Organization'))
const OrgAccounts = lazy(() => import('./pages/OrgAccounts/OrgAccounts'))
const OrgAccountAdmin = lazy(
  () => import('./pages/OrgAccounts/OrgAccountAdmin')
)
const InvoiceRecipients = lazy(
  () => import('./pages/InvoiceRecipients/InvoiceRecipients')
)
const InvoiceRecipient = lazy(
  () => import('./pages/InvoiceRecipients/InvoiceRecipient')
)
const InvoiceRecipientAdmin = lazy(
  () => import('./pages/InvoiceRecipientsAdmin/InvoiceRecipientAdmin')
)
const Purchasers = lazy(() => import('./pages/Purchasers/Purchasers'))
const Purchaser = lazy(() => import('./pages/Purchasers/Purchaser'))
const Transactions = lazy(() => import('./pages/Transactions/Transactions'))
const Transaction = lazy(() => import('./pages/Transactions/Transaction'))
const Invoices = lazy(() => import('./pages/Invoices/Invoices'))

type Dependencies =  {
  config: ConfigType
  locale: Locale
  browserHistory: History
}

const AppRoutes: React.FC<Dependencies> = ({
  config,
  locale,
}) => {
  const login = useSelector((state: State) => state.login as LoginState)
  const contextValue = useMemo(
    () => ({
      config,
      locale,
    }),
    [config, locale]
  )

  const secureProps = {
    secure: true,
    login: login.data,
  }


  // Page loader component in page frame to prevent loading flicker
  const asyncLoader = (
    <PageFrame>
      <Loader local />
    </PageFrame>
  )


  return (
    <Context.Provider value={contextValue}>
      <LastLocationProvider >  
          <ErrorBoundary
            active
            showErrors={false}
            alertComponent={Alert}
            wrapComponent={ErrorBoundaryWrapper}
          >
            <Suspense fallback={asyncLoader}>
              <Routes>
                <Route
                  path="/mmba"
                  element={
                    <SecureRoute mmbAdmin >
                      <Dashboard />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/authenticate"
                  element={
                    <SecureRoute>
                      <Authenticate />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/valj_orgkonto"
                  element={
                    <SecureRoute>
                      <SelectOrgAccount />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/logga_ut"
                  element={
                    <SecureRoute >
                      <Logout />
                    </SecureRoute>
                  }
                />
                <Route
                  index
                  element={
                    <SecureRoute {...secureProps}>
                      <Dashboard />
                     </SecureRoute>
                  }
                />
                <Route
                  path="/organisation"
                  element={
                    <SecureRoute {...secureProps}>
                      <Organization />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/organisation/admin/visa/:id"
                  element={
                    <SecureRoute {...secureProps}>
                      <OrganizationAdmin />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/organisation/admin/andra/:id"
                  element={
                    <SecureRoute access={AdminTypes.ORG_ADMIN} {...secureProps}>
                      <OrganizationAdmin editing />
                    </SecureRoute>
                  }
                />

                <Route
                  path="/organisation/admin/ny"
                  element={
                    <SecureRoute access={AdminTypes.ORG_ADMIN} {...secureProps}>
                      <OrganizationAdmin editing />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/orgkonto"
                  element={
                    <SecureRoute {...secureProps}>
                      <OrgAccounts />
                    </SecureRoute>
                  }
                />

                <Route
                  path="/orgkonto/ny"
                  element={
                    <SecureRoute access={AdminTypes.ORG_ADMIN} {...secureProps}>
                      <OrgAccounts creating />
                    </SecureRoute>
                  }
                />

                <Route
                  path="/orgkonto/andra"
                  element={
                    <SecureRoute {...secureProps}>
                      <OrgAccounts editing />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/orgkonto/orgadmin/visa/:id"
                  element={
                    <SecureRoute {...secureProps}>
                      <OrgAccountAdmin />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/orgkonto/orgadmin/andra/:id"
                  element={
                    <SecureRoute access={AdminTypes.ACCOUNT_ADMIN} {...secureProps}>
                      <OrgAccountAdmin editing />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/orgkonto/orgadmin/ny"
                  element={
                    <SecureRoute access={AdminTypes.ACCOUNT_ADMIN} {...secureProps}>
                      <OrgAccountAdmin editing />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/fakturamottagare"
                  element={
                    <SecureRoute {...secureProps}>
                      <InvoiceRecipients />
                    </SecureRoute>
                  }
                />

                <Route
                  path="/fakturamottagare/visa/:id"
                  element={
                    <SecureRoute {...secureProps}>
                      <InvoiceRecipient />
                    </SecureRoute>
                  }
                />

                <Route
                  path="/fakturamottagare/ny"
                  element={
                    <SecureRoute {...secureProps}>
                      <InvoiceRecipient editing />
                    </SecureRoute>
                  }
                />

                <Route
                  path="/fakturamottagare/andra/:id"
                  element={
                    <SecureRoute {...secureProps}>
                      <InvoiceRecipient editing />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/fakturamottagaradministrator/visa/:invoiceRecipientId/:id"
                  element={
                    <SecureRoute
                      toggledByFeature="invoiceRecipientAdmins"
                      {...secureProps}
                    >
                      <InvoiceRecipientAdmin />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/fakturamottagaradministrator/ny/:invoiceRecipientId"
                  element={
                    <SecureRoute
                      toggledByFeature="invoiceRecipientAdmins"
                      {...secureProps}
                    >
                      <InvoiceRecipientAdmin editing />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/fakturamottagaradministrator/andra/:invoiceRecipientId/:id"
                  element={
                    <SecureRoute
                      toggledByFeature="invoiceRecipientAdmins"
                      {...secureProps}
                    >
                      <InvoiceRecipientAdmin editing />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/kopare"
                  element={
                    <SecureRoute {...secureProps}>
                      <Purchasers />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/kopare/visa/:id"
                  element={
                    <SecureRoute {...secureProps}>
                      <Purchaser action={FormActionType.VIEWING} />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/kopare/andra/:id"
                  element={
                    <SecureRoute {...secureProps}>
                      <Purchaser action={FormActionType.UPDATING} />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/kopare/ny"
                  element={
                    <SecureRoute {...secureProps}>
                      <Purchaser action={FormActionType.CREATING} />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/kop"
                  element={
                    <SecureRoute {...secureProps}>
                      <Transactions />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/kop/:id"
                  element={
                    <SecureRoute {...secureProps}>
                      <Transaction />
                    </SecureRoute>
                  }
                />

                <Route
                  path="/faktura"
                  element={
                    <SecureRoute {...secureProps}>
                      <Invoices />
                    </SecureRoute>
                  }
                />

                <Route
                  path="/faktura/:id"
                  element={
                    <SecureRoute {...secureProps}>
                      <AsyncComponent path="pages/Invoices/Invoice" />
                    </SecureRoute>
                  }
                />

                <Route path="*" element={<Error />} />
                <Route path="fel" element={<Error />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </LastLocationProvider>
    </Context.Provider>
  )
}

const ErrorBoundaryWrapper: React.FunctionComponent<any> = (props) => {
  return (
    <PageFrame header={false} footer={false}>
      <div className="l-flex--centered-child">
        <div className="Box l-max-width--30 l-max-height--100% u-overflow-scroll--y">
          {props.children}
          <div className="l-margin--t l-align--center">
            <a href="/admin/" className="button">
              <Text>general.back</Text>
            </a>
          </div>
        </div>
      </div>
    </PageFrame>
  )
}

export default AppRoutes
